import type { FieldInputADTValue, InputValueItem, SubFormColumn } from '@lighthouse/core'
import {
    createFieldInputFileFilter,
    FieldInput,
    getFieldInputError,
    getFieldInputRules,
    getWithScopeIdAndRecordIdAndColumnId,
    notNeedBlurInputTypes
} from '@lighthouse/shared'
import { find } from 'rambda'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import { useSubFormFieldInputContext } from './SubFormFieldInputProvider'

interface SubFormCellProps {
    value: InputValueItem
    column: SubFormColumn
    recordId: string
}

const SCxCell = styled.div<{ width: number }>`
    width: ${({ width }) => width}px;
    /* align-items: center; */
`

export const SubFormCell: React.FunctionComponent<SubFormCellProps> = ({ column, value, recordId }) => {
    const { config, id: columnId, width } = column
    const { fieldPointer, inputType, title: configTitle } = config
    const fieldInputProps = useSubFormFieldInputContext()
    const {
        blockId,
        scope,
        viewRecordId,
        uploadyOptions,
        richTextUploadOptions,
        errors,
        columnsWidth,
        dataSource,
        submitAttempted,
        onCellChange,
        onCellBlur,
        onChangeSmsCode,
        reportCellError,
        resetCellServiceError
    } = fieldInputProps
    const field = useMemo(() => dataSource?.schema?.[fieldPointer ?? ''], [dataSource?.schema, fieldPointer])

    const [touched, setTouched] = useState(false)
    const [lastValidationError, setLastValidationError] = useState<ReturnType<typeof getFieldInputError> | null>(null)
    const hasUserChangedRef = useRef(false)

    const showErrors = touched || (submitAttempted ?? false)
    const displayError = showErrors ? lastValidationError ?? undefined : undefined

    const fieldInputConfig = useMemo(() => {
        return {
            ...config,
            showTitle: false,
            uploadyOptions:
                fieldPointer && uploadyOptions
                    ? {
                          ...uploadyOptions,
                          options: {
                              ...uploadyOptions.options,
                              fileFilter: createFieldInputFileFilter({
                                  fieldType: field?.type,
                                  fieldId: field?.id,
                                  config: inputType === 'file' ? (config as any).file : undefined
                              })
                          }
                      }
                    : undefined,
            richTextUploadOptions: fieldPointer ? richTextUploadOptions : undefined
        }
    }, [config, fieldPointer, uploadyOptions, field?.type, field?.id, inputType, richTextUploadOptions])

    const id = useMemo(
        () => getWithScopeIdAndRecordIdAndColumnId(blockId, scope, viewRecordId, columnId),
        [blockId, columnId, scope, viewRecordId]
    )

    const validateAndReport = useCallback(
        (v: FieldInputADTValue) => {
            if (!field) {
                return
            }
            const fieldError = getFieldInputError({
                value: v,
                rule: getFieldInputRules({ config: config, id: columnId, title: configTitle ?? '' }),
                fieldType: field.type
            })
            const hasError = Object.keys(fieldError).length > 0
            setLastValidationError(hasError ? fieldError : null)
            reportCellError?.(recordId, columnId, hasError ? fieldError : undefined)
            return fieldError
        },
        [columnId, config, configTitle, field, recordId, reportCellError]
    )

    // 挂载时验证：只向表单容器报告初始校验状态，不更新显示用的 lastValidationError
    // 卸载时清除错误，避免被卸载的 cell 残留错误影响表单提交校验
    useEffect(() => {
        if (!field || !reportCellError) {
            return
        }
        const v = { type: inputType, value: value?.value } as FieldInputADTValue
        const fieldError = getFieldInputError({
            value: v,
            rule: getFieldInputRules({ config: config, id: columnId, title: configTitle ?? '' }),
            fieldType: field.type
        })
        const hasError = Object.keys(fieldError).length > 0
        reportCellError(recordId, columnId, hasError ? fieldError : undefined)
        return () => {
            reportCellError(recordId, columnId, undefined)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // submitAttempted 从 false 变为 true 时，触发一次完整校验（含显示）
    const prevSubmitAttemptedRef = useRef(submitAttempted)
    useEffect(() => {
        if (submitAttempted && !prevSubmitAttemptedRef.current) {
            validateAndReport({ type: inputType, value: value?.value } as FieldInputADTValue)
        }
        prevSubmitAttemptedRef.current = submitAttempted
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitAttempted])

    // 监听外部 service error 变化，覆盖本地 lastValidationError
    const externalError = useMemo(
        () => find(v => recordId === v.recordId && v.columnId === columnId, errors ?? [])?.error,
        [errors, recordId, columnId]
    )
    const prevExternalErrorRef = useRef(externalError)
    useEffect(() => {
        if (externalError !== prevExternalErrorRef.current) {
            prevExternalErrorRef.current = externalError
            if (externalError) {
                setLastValidationError(externalError)
                setTouched(true)
            }
        }
    }, [externalError])

    const handleSaveChange = useCallback(
        (fieldValue: FieldInputADTValue) => {
            setTouched(true)
            hasUserChangedRef.current = true
            resetCellServiceError?.(recordId, columnId)
            validateAndReport(fieldValue)
            onCellChange?.(recordId, columnId, fieldValue)
            if (notNeedBlurInputTypes.has(fieldValue.type)) {
                onCellBlur?.(recordId, columnId)
            }
        },
        [onCellChange, onCellBlur, columnId, recordId, validateAndReport, resetCellServiceError]
    )

    const handleBlur = useCallback(
        (v: FieldInputADTValue) => {
            setTouched(true)
            validateAndReport(v)
            onCellBlur?.(recordId, columnId)
        },
        [onCellBlur, columnId, recordId, validateAndReport]
    )

    const handleSmsCodeChange = useCallback(
        (v: string) => {
            onChangeSmsCode?.(recordId, columnId, v)
        },
        [columnId, onChangeSmsCode, recordId]
    )

    return (
        <SCxCell
            key={columnId}
            style={fieldInputConfig.inputType === 'checkbox' ? { alignItems: 'center', display: 'flex' } : undefined}
            width={columnsWidth?.[columnId] || width}
        >
            <FieldInput
                id={id}
                value={value?.value}
                config={fieldInputConfig}
                error={displayError}
                {...fieldInputProps}
                onChange={handleSaveChange}
                onBlur={handleBlur}
                onChangeSmsCode={handleSmsCodeChange}
            />
        </SCxCell>
    )
}
